import { useEffect, useRef } from 'react';

import { fetchEntitiesForWorklist } from '@/api/poisApi';
import type { MapSearchParams } from '@/components/map/utils/mapUrlState';
import type { PoiFeatureProperties } from '@/components/map/utils/poiFeatureProperties';
import { showErrorToast } from '@/errors/showErrorToast';
import { toLngLatTuple } from '@/utils/mapUtils';

/**
 * Selects the record a Find & Fix URL names on arrival (`?client&poi&group`, as the Network
 * Explorer's "Open in Find & Fix" hand-off writes them).
 *
 * The target is captured once, at mount: the map's own URL writes replace the whole search,
 * so the params are gone after the first viewport write. The selection waits until the
 * client the URL names is the selected one (a new tab boots on it from the URL; the in-tab
 * fallback switches client before it navigates, and the switch lands a render later) and the
 * map is in entity mode and loaded,
 * then fetches that client's worklist once and selects the matching feature — a fresh,
 * complete record from the client's own feed rather than anything carried across. A record
 * the user has already selected by then is left alone.
 */
export function useDeepLinkedEntitySelection(
    search: Pick<MapSearchParams, 'client' | 'poi' | 'group'>,
    selectedClientId: string | null,
    enabled: boolean,
    hasSelection: boolean,
    onSelect: (poi: PoiFeatureProperties, coords?: [number, number]) => void,
): void {
    const targetRef = useRef(
        search.client && search.poi && search.group
            ? { client: search.client, poi: search.poi, group: search.group }
            : null,
    );
    const doneRef = useRef(false);

    const hasSelectionRef = useRef(hasSelection);
    useEffect(() => {
        hasSelectionRef.current = hasSelection;
    }, [hasSelection]);

    const onSelectRef = useRef(onSelect);
    useEffect(() => {
        onSelectRef.current = onSelect;
    }, [onSelect]);

    useEffect(() => {
        const target = targetRef.current;
        if (!target || doneRef.current || !enabled) return;
        if (selectedClientId !== target.client) return;
        doneRef.current = true;

        let cancelled = false;
        fetchEntitiesForWorklist(target.client, target.group)
            .then((collection) => {
                if (cancelled || hasSelectionRef.current) return;
                const hit = collection.features.find(
                    (f) => (f.properties as PoiFeatureProperties | null)?.id === target.poi,
                );
                if (!hit) {
                    showErrorToast({
                        code: 'not_found',
                        title: 'Not found',
                        message: 'That record could not be found in this network.',
                    });
                    return;
                }
                const coords = hit.geometry.type === 'Point' ? toLngLatTuple(hit.geometry.coordinates) : undefined;
                onSelectRef.current(hit.properties as PoiFeatureProperties, coords);
            })
            .catch((err: unknown) => {
                if (!cancelled) showErrorToast(err);
            });
        return () => {
            cancelled = true;
        };
    }, [enabled, selectedClientId]);
}
